import React, { useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import {
  Box, Button, Typography, TextField, CircularProgress, Chip, Collapse,
} from "@mui/material";
import { toast } from "react-toastify";
import { RootState } from "&store/store";
import { twoFactorActions } from "./twoFactor.slice";
import { loginActions } from "&features/login/login.slice";
import { usersActions } from "&features/users/users.slice";

type ReduxProps = ConnectedProps<typeof connector>;

const SetupAuthenticatorComponent = (props: ReduxProps) => {
  const { getStatus, generate, verifySetup, remove, logout, logoutUser } = props;

  const [status, setStatus] = useState<any>(null);
  const [step, setStep] = useState<"idle" | "qr">("idle");
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [otp, setOtp] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const { payload }: any = await getStatus();
      if (payload) setStatus(payload);
    })();
  }, []);

  const handleSetup = async () => {
    setLoading(true);
    const { payload }: any = await generate();
    setLoading(false);
    if (payload?.qrDataUrl) {
      setQrDataUrl(payload.qrDataUrl);
      setSecret(payload.secret);
      setStep("qr");
    }
  };

  const handleVerify = async () => {
    if (!otp || otp.length !== 6) {
      toast.error("Please enter a valid 6-digit code", { position: "top-center" });
      return;
    }
    setLoading(true);
    const { payload, error }: any = await verifySetup({ otp });
    setLoading(false);
    if (error || !payload?.message) return;
    toast.success("Google Authenticator activated! Please log in again.", { position: "top-center", autoClose: 1500 });
    setTimeout(async () => { await logoutUser(""); logout(); }, 1500);
  };

  const handleRemove = async () => {
    if (!window.confirm("Remove Google Authenticator from your account?")) return;
    setLoading(true);
    const { payload }: any = await remove();
    setLoading(false);
    if (payload?.message) {
      toast.success(payload.message, { position: "top-center" });
      setStatus({ exists: false, isVerified: false, status: null });
    }
  };

  const getStatusChip = () => {
    if (!status?.exists) return <Chip label="Not Configured" color="default" size="small" />;
    if (status.isVerified && status.status === "Active") return <Chip label="Active" color="success" size="small" />;
    return <Chip label="Pending Setup" color="warning" size="small" />;
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: "1rem" }}>
        <Typography variant="subtitle1" fontWeight="bold">Google Authenticator</Typography>
        {status && getStatusChip()}
      </Box>

      {step === "idle" && (
        <>
          {status?.isVerified && status?.status === "Active" ? (
            <Button variant="outlined" color="error" onClick={handleRemove} disabled={loading} size="small">
              {loading ? <CircularProgress size={18} /> : "Remove Authenticator"}
            </Button>
          ) : (
            <Button variant="outlined" onClick={handleSetup} disabled={loading} size="small">
              {loading ? <CircularProgress size={18} /> : status?.exists ? "Restart Setup" : "Set Up Google Authenticator"}
            </Button>
          )}
        </>
      )}

      {step === "qr" && (
        <Box>
          <Typography variant="body2" sx={{ marginBottom: "1rem" }}>
            Scan this QR code with Google Authenticator, then enter the 6-digit code below.
          </Typography>
          {qrDataUrl && (
            <Box sx={{ textAlign: "center", marginBottom: "1rem" }}>
              <img src={qrDataUrl} alt="QR Code" style={{ width: 180, height: 180 }} />
            </Box>
          )}
          <Button size="small" onClick={() => setShowSecret(!showSecret)} sx={{ marginBottom: "0.5rem" }}>
            {showSecret ? "Hide" : "Show"} manual entry key
          </Button>
          <Collapse in={showSecret}>
            <Typography
              variant="caption"
              sx={{
                display: "block", fontFamily: "monospace", background: "#f5f5f5",
                padding: "0.5rem", borderRadius: 1, marginBottom: "1rem", wordBreak: "break-all",
              }}
            >
              {secret}
            </Typography>
          </Collapse>
          <TextField
            fullWidth
            label="Enter 6-digit code"
            placeholder="000000"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputProps={{ maxLength: 6 }}
            sx={{ marginBottom: "1rem" }}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="contained" onClick={handleVerify} disabled={loading} size="small">
              {loading ? <CircularProgress size={18} /> : "Verify & Activate"}
            </Button>
            <Button variant="text" onClick={() => { setStep("idle"); setOtp(""); }} size="small">
              Cancel
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const mapStateToProps = (_: RootState) => ({});

const mapDispatchToProps = {
  getStatus: twoFactorActions.getAuthenticatorStatus,
  generate: twoFactorActions.generateAuthenticator,
  verifySetup: twoFactorActions.verifySetupOtp,
  remove: twoFactorActions.removeAuthenticator,
  logoutUser: usersActions.logoutUser,
  logout: loginActions.reset,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
const SetupAuthenticatorRedux = connector(SetupAuthenticatorComponent);
export { SetupAuthenticatorRedux as SetupAuthenticatorComponent };
